import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { HealthCheck } from '@gonfalon/health-check/HealthCheck';
import { HealthCheckButton } from '@gonfalon/health-check/HealthCheckButton/HealthCheckButton';
import { HealthCheckCard } from '@gonfalon/health-check/HealthCheckCard/HealthCheckCard';
import { HealthCheckIcon } from '@gonfalon/health-check/HealthCheckIcon';
import { HealthCheckItem } from '@gonfalon/health-check/HealthCheckItem/HealthCheckItem';
import type { HealthCheckStatus } from '@gonfalon/health-check/types';

import { trackMetricsEvent } from '../analytics';

import type { MetricDefinition } from './constants';
import { HealthIndicatorSkeleton } from './HealthIndicatorSkeleton';
import { useHealthIndicator } from './useHealthIndicator';

export interface HealthIndicatorProps {
  kind?: 'popover' | 'card' | 'icon' | 'badge';
  metric: MetricDefinition;
}

export const HealthIndicator: React.FC<HealthIndicatorProps> = ({ kind = 'popover', metric }) => {
  const healthIndicatorData = useHealthIndicator(metric);
  const documentationUrl = getDocumentationUrl('/home/metrics/metric-health-checks');
  const { overallStatus, statusEntries } = healthIndicatorData;

  switch (kind) {
    case 'popover':
      return (
        <HealthCheck
          status={overallStatus}
          documentationUrl={documentationUrl}
          onReload={healthIndicatorData.refetch}
          onButtonClick={() =>
            trackMetricsEvent('Metric Health Check Button Clicked', {
              eventKey: metric?.eventKey || '',
              versionId: metric?._versionId || '',
              status: overallStatus,
              component: 'MetricHealthCheck',
              type: 'popover',
            })
          }
          onDocumentationLinkClick={() =>
            trackMetricsEvent('Metric Health Check Documentation Link Clicked', {
              eventKey: metric?.eventKey || '',
              versionId: metric?._versionId || '',
              status: overallStatus,
              component: 'MetricHealthCheck',
              type: 'popover',
            })
          }
        >
          <>
            {statusEntries.map(
              (item, index) =>
                item.message && (
                  <HealthCheckItem key={index} status={item.status as HealthCheckStatus}>
                    {item.message}
                  </HealthCheckItem>
                ),
            )}
          </>
        </HealthCheck>
      );
    case 'card':
      return (
        <HealthCheckCard
          showStatus
          status={overallStatus}
          documentationUrl={documentationUrl}
          onDocumentationLinkClick={() =>
            trackMetricsEvent('Metric Health Check Documentation Link Clicked', {
              eventKey: metric?.eventKey || '',
              versionId: metric?._versionId || '',
              status: overallStatus,
              component: 'MetricHealthCheck',
              type: 'card',
            })
          }
        >
          {overallStatus === 'loading' ? (
            <HealthIndicatorSkeleton />
          ) : (
            <>
              {statusEntries.map(
                (item, index) =>
                  item.message && (
                    <HealthCheckItem key={index} status={item.status as HealthCheckStatus}>
                      {item.message}
                    </HealthCheckItem>
                  ),
              )}
            </>
          )}
        </HealthCheckCard>
      );
    case 'badge':
      return <HealthCheckButton status={overallStatus} />;
    case 'icon':
      return <HealthCheckIcon status={overallStatus} showTooltip />;
    default:
      return null;
  }
};
